import { z } from "zod";

const subscriptionChainItemSchema = z.object({
  planName: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  coverageDays: z.number(),
});
export type SubscriptionChainItem = z.infer<typeof subscriptionChainItemSchema>;

const subscriptionClientSchema = z.object({
  publicId: z.string().optional(),
  firstName: z.string(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  businessName: z.string().nullish(),
});

const subscriptionPlanSchema = z.object({
  publicId: z.string().optional(),
  name: z.string(),
  price: z.number().optional(),
  classCount: z.number().nullable().optional(),
});

const subscriptionPaymentSchema = z.object({
  publicId: z.string(),
  amount: z.number(),
  ticketNumber: z.number().nullable(),
  paymentMethodName: z.string(),
  sellBy: z.string().optional(),
  createdAt: z.string(),
});

const subscriptionStatusEnum = z.enum([
  "ACTIVE",
  "FROZEN",
  "PENDING",
  "EXPIRED",
  "CANCELLED",
]);

/**
 * Congelamiento vigente o programado. El backend lo limpia al reanudar, así
 * que `freeze !== null` significa "hay una pausa activa o agendada"; el
 * historial completo vive en el tab de historial.
 */
const subscriptionFreezeSchema = z.object({
  startDate: z.string(),
  endDate: z.string().nullable(),
  reason: z.string().nullable(),
  frozenBy: z.string().nullable(),
});
export type SubscriptionFreeze = z.infer<typeof subscriptionFreezeSchema>;

export const subscriptionSchema = z.object({
  publicId: z.string(),
  startDate: z.string().optional(),
  endDate: z.string(),
  status: subscriptionStatusEnum.optional(),
  freeze: subscriptionFreezeSchema.nullish(),
  remainingDays: z.number(),
  remainingClasses: z.number().nullable().optional(),
  hasPendingRenewal: z.boolean().optional().default(false),
  isAutoRenewing: z.boolean().optional().default(false),
  chain: z.array(subscriptionChainItemSchema).optional().default([]),
  totalCoverageDays: z.number().optional().default(0),
  client: subscriptionClientSchema,
  plan: subscriptionPlanSchema,
  payment: subscriptionPaymentSchema.optional(),
});
export type Subscription = z.infer<typeof subscriptionSchema>;

/** Item shape returned for STAFF role (no price, ticket, payment method, email). */
export const subscriptionStaffSchema = z.object({
  publicId: z.string(),
  client: z.object({
    firstName: z.string(),
    lastName: z.string().nullish(),
    businessName: z.string().nullish(),
  }),
  plan: z.object({
    name: z.string(),
    classCount: z.number().nullable().optional(),
  }),
  status: subscriptionStatusEnum,
  freeze: subscriptionFreezeSchema.nullish(),
  endDate: z.string(),
  remainingDays: z.number(),
  remainingClasses: z.number().nullable().optional(),
  hasPendingRenewal: z.boolean().optional().default(false),
  isAutoRenewing: z.boolean().optional().default(false),
  chain: z.array(subscriptionChainItemSchema).optional().default([]),
  totalCoverageDays: z.number().optional().default(0),
});
export type SubscriptionStaff = z.infer<typeof subscriptionStaffSchema>;

export type SubscriptionRow = Subscription | SubscriptionStaff;

export function isSubscriptionStaff(
  row: SubscriptionRow,
): row is SubscriptionStaff {
  return "status" in row && "remainingDays" in row && !("payment" in row);
}

export const updateSubscriptionPayloadSchema = z
  .object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  })
  .refine(
    (value) =>
      value.startDate !== undefined || value.endDate !== undefined,
    { message: "Debes modificar al menos un campo" },
  );
export type UpdateSubscriptionPayload = z.infer<
  typeof updateSubscriptionPayloadSchema
>;

/** Fechas como día calendario (YYYY-MM-DD): es lo que emite el DatePicker. */
const calendarDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Fecha inválida");

export const freezeSubscriptionPayloadSchema = z
  .object({
    startDate: calendarDay.optional(),
    endDate: calendarDay.nullable().optional(),
    reason: z
      .string()
      .trim()
      .min(3, "El motivo debe tener al menos 3 caracteres")
      .max(500, "El motivo no puede superar los 500 caracteres"),
  })
  .refine(
    (value) =>
      !value.startDate ||
      !value.endDate ||
      value.endDate > value.startDate,
    {
      message: "La fecha de término debe ser posterior a la de inicio",
      path: ["endDate"],
    },
  );
export type FreezeSubscriptionPayload = z.infer<
  typeof freezeSubscriptionPayloadSchema
>;

export const unfreezeSubscriptionResponseSchema = z.object({
  publicId: z.string(),
  frozenDays: z.number(),
  endDate: z.string(),
});

const rawActionSchema = z.enum(["INSERT", "UPDATE", "DELETE"]);

const rawSubscriptionHistoryEntrySchema = z.object({
  publicId: z.string(),
  action: rawActionSchema,
  performedAt: z.string().optional(),
  performedBy: z.string().optional(),
  validFrom: z.string().optional(),
  changedBy: z.string().optional(),
  oldData: z.record(z.string(), z.unknown()).nullable(),
  newData: z.record(z.string(), z.unknown()).nullable(),
  recordId: z.number().optional(),
});

export const subscriptionHistoryEntrySchema = rawSubscriptionHistoryEntrySchema
  .transform((raw) => ({
    publicId: raw.publicId,
    action: raw.action,
    performedAt: raw.performedAt ?? raw.validFrom ?? "",
    performedBy: raw.performedBy ?? raw.changedBy ?? "",
    oldData: raw.oldData,
    newData: raw.newData,
    recordId: raw.recordId,
  }));
export type SubscriptionHistoryEntry = z.infer<
  typeof subscriptionHistoryEntrySchema
>;

export const subscriptionHistoryMetaSchema = z.object({
  totalCount: z.number().int().nonnegative(),
  totalPages: z.number().int().positive(),
  currentPage: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type SubscriptionHistoryMeta = z.infer<
  typeof subscriptionHistoryMetaSchema
>;

export const subscriptionHistoryResponseSchema = z.union([
  // Forma paginada actual: { data, meta }
  z.object({
    data: z.array(subscriptionHistoryEntrySchema),
    meta: subscriptionHistoryMetaSchema,
  }),
  // Compat: el backend antes devolvía sólo { data: [] } o un array plano.
  // Si llega así, sintetizamos meta tratando todo como una sola página.
  z
    .object({ data: z.array(subscriptionHistoryEntrySchema) })
    .transform((wrapped) => ({
      data: wrapped.data,
      meta: {
        totalCount: wrapped.data.length,
        totalPages: 1,
        currentPage: 1,
        pageSize: Math.max(1, wrapped.data.length),
      },
    })),
  z.array(subscriptionHistoryEntrySchema).transform((data) => ({
    data,
    meta: {
      totalCount: data.length,
      totalPages: 1,
      currentPage: 1,
      pageSize: Math.max(1, data.length),
    },
  })),
]);
export type SubscriptionHistoryResponse = z.infer<
  typeof subscriptionHistoryResponseSchema
>;

const subscriptionDeleteBookingBlockerSchema = z.object({
  publicId: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  planName: z.string().nullable().optional(),
});
export type SubscriptionDeleteBookingBlocker = z.infer<
  typeof subscriptionDeleteBookingBlockerSchema
>;

export const subscriptionDeleteConflictSchema = z.discriminatedUnion("code", [
  z.object({
    code: z.literal("BOOKINGS_EXIST"),
    bookings: z.array(subscriptionDeleteBookingBlockerSchema),
    truncated: z.boolean().optional(),
  }),
  z.object({
    code: z.literal("AUTO_RENEWAL_ACTIVE"),
    gatewaySubscriptionPublicId: z.string(),
  }),
]);
export type SubscriptionDeleteConflict = z.infer<
  typeof subscriptionDeleteConflictSchema
>;

/**
 * 409 estructurado del PATCH. Hoy sólo `AUTO_RENEWAL_ACTIVE` se devuelve con
 * shape tipada; los otros 409 (overlap, future bookings) llegan como
 * `{ error: string }` y se mapean a un toast genérico.
 */
export const subscriptionUpdateConflictSchema = z.object({
  code: z.literal("AUTO_RENEWAL_ACTIVE"),
  gatewaySubscriptionPublicId: z.string(),
});
export type SubscriptionUpdateConflict = z.infer<
  typeof subscriptionUpdateConflictSchema
>;
